import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const LearningHubSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  const features = [
    {
      id: 1,
      title: "LEARN",
      subtitle: "Gain in-depth knowledge with expert-led courses, hands-on training and get certificate.",
      icon: "BookOpen",
      color: "from-blue-500 to-blue-600",
      benefits: [
        "Expert-led live sessions",
        "Hands-on practical training",
        "Industry-recognized certificates",
        "24/7 learning support"
      ]
    },
    {
      id: 2,
      title: "DESIGN",
      subtitle: "Create stunning visuals, user-friendly interfaces, and innovative experiences.",
      icon: "Palette",
      color: "from-purple-500 to-purple-600",
      benefits: [
        "UI/UX design principles",
        "Creative design tools",
        "User experience optimization",
        "Portfolio development"
      ]
    },
    {
      id: 3,
      title: "DEVELOP",
      subtitle: "Build powerful applications, websites, and cutting-edge technology solutions.",
      icon: "Code",
      color: "from-green-500 to-green-600",
      benefits: [
        "Full-stack development",
        "Modern frameworks & tools",
        "Real-world projects",
        "Industry best practices"
      ]
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    const element = document.getElementById('learning-hub');
    if (element) observer?.observe(element);

    return () => observer?.disconnect();
  }, []);

  return (
    <section id="learning-hub" className="py-20 bg-gradient-to-b from-surface to-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-6">
            <span className=" text-nipix-blue">L</span>
            <span className="text-text-primary">earning </span>
            <span className="text-nipix-blue">H</span>
            <span className="text-text-primary">ub</span>
          </h2>
          <p className="text-xl text-text-secondary text-gradient font-semibold mb-4 max-w-4xl mx-auto leading-relaxed">
            Learn Anytime, Design Everywhere, Develop Anywhere.
          </p>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Access expert-led courses anytime, anywhere, and take your learning to the next level.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {features?.map((feature, index) => (
            <motion.div
              key={feature?.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="group relative h-[500px]"
            >
              <div className="bg-white rounded-2xl shadow-lg border border-border overflow-hidden transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 h-full flex flex-col">
                
                {/* Header Area with Title */}
<div className="relative h-40 flex items-center justify-center bg-black">
  <h3
    className={`text-3xl font-bold bg-gradient-to-r ${feature?.color} bg-clip-text text-transparent`}
  >
    {feature?.title}
  </h3>
</div>


                {/* Feature Content */}
                <div className="p-6 flex-1 flex flex-col">
                  {/* Description */}
                  <p className="text-text-secondary mb-6 leading-relaxed flex-shrink-0">
                    {feature?.subtitle}
                  </p>

                  {/* Benefits */}
                  <div className="space-y-3 mb-6 flex-1">
                    {feature?.benefits?.map((benefit, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <div className="w-5 h-5 bg-success/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <Icon name="Check" size={12} className="text-success" strokeWidth={2} />
                        </div>
                        <span className="text-sm text-text-secondary">{benefit}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={() => {
                      const element = document.getElementById('courses');
                      if (element) {
                        const headerHeight = 80;
                        const elementPosition = element?.getBoundingClientRect()?.top;
                        const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
                        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                      }
                    }}
                    className={`w-full bg-gradient-to-r ${feature?.color} text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity duration-300 flex items-center justify-center space-x-2 flex-shrink-0`}
                  >
                    <span>Explore {feature?.title}</span>
                    <Icon name="ArrowRight" size={16} strokeWidth={2} />  
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA 
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-center mt-16"
        >
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-border">
            <Icon name="Zap" size={48} className="mx-auto mb-4 text-primary" strokeWidth={2} />
            <h3 className="text-2xl font-bold text-text-primary mb-4">Ready to Start Your Learning Journey?</h3>
            <p className="text-lg text-text-secondary mb-6 max-w-2xl mx-auto">
              Join thousands of learners who are already building their future with our comprehensive learning platform.
            </p>
            <button
              onClick={() => {
                const element = document.getElementById('apply-section');
                if (element) {
                  const headerHeight = 80;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
                  window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                }
              }}
              className="bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-primary/90 transition-colors duration-300 inline-flex items-center space-x-2"
            >
              <Icon name="Play" size={20} strokeWidth={2} />
              <span>Start Learning Now</span>
            </button>
          </div>
        </motion.div>*/}
      </div>
    </section>
  );
};

export default LearningHubSection;
